// The requirements round, as a form.
//
// `ask_user` asks up to four questions at once, before the agent plans anything,
// and the whole round is answered in ONE request. This module owns the draft state
// and the submit payload; `components/AgentQuestionCard.js` renders it.
//
// Pure and separate for the `chatRounds.js` / `chatModes.js` reason: the components
// that consume it (`ChatPanel`, and `Dashboard`'s thread) cannot be rendered under
// jest, and this is the half whose behaviour matters — a draft that loses the user's
// typing, or a payload that drops an answer, is a silent failure.
//
// The shape it works on comes from the backend verbatim (see
// `state.pending_questions_of`):
//
//   {questionId, header, question, why, options: [{label, description}],
//    multiSelect, allowOther, askedAt}

// What the user typed when none of the options fit. A sentinel rather than a
// separate boolean per question, so one `draft` map holds both kinds of answer.
export const OTHER = '__other__';

// Multi-select answers are joined with this, and the backend splits on it to check
// each part against the offered labels. CHANGE-BOTH with the `/questions` route.
const JOIN = ', ';

/** Every question the run is waiting on, in the order it asked. */
export function pendingQuestions(run) {
  const list = run?.pendingQuestions;
  return Array.isArray(list) ? list.filter((q) => q && q.question) : [];
}

/** True when this run is holding a round the user has not answered. */
export function hasQuestions(run) {
  return pendingQuestions(run).length > 0;
}

/**
 * The empty draft: one entry per question.
 *
 * `selected` is an array even for a single-select question, so the two modes share
 * one shape and the card does not need a branch per control type. `other` holds the
 * free text separately from the selection, which is what lets the user type
 * something, click an option, and click back to Other without losing what they
 * typed — the thing a single `value` field cannot do.
 */
export function blankDraft(questions) {
  const draft = {};
  for (const q of questions || []) {
    draft[q.questionId] = { selected: [], other: '', useOther: false };
  }
  return draft;
}

/** Toggle (multi-select) or set (single-select) one option. */
export function chooseOption(entry, label, multiSelect) {
  const current = entry?.selected || [];
  if (!multiSelect) {
    // Clicking the chosen option again does NOT clear it: a required question with
    // nothing selected is a dead end the user cannot see the cause of.
    return { ...entry, selected: [label], useOther: false };
  }
  const selected = current.includes(label)
    ? current.filter((l) => l !== label)
    : [...current, label];
  return { ...entry, selected, useOther: false };
}

/** Switch to (or away from) the free-text answer. */
export function chooseOther(entry, useOther) {
  return { ...entry, useOther, selected: useOther ? [] : entry?.selected || [] };
}

export function setOtherText(entry, other) {
  // Typing implies choosing it. Otherwise the user types an answer, presses Submit
  // and it is silently dropped because a radio was never clicked.
  return { ...entry, other, useOther: true };
}

/** One question's answer as the string the backend receives, or '' for none. */
export function answerFor(question, entry) {
  if (!question || !entry) return '';
  if (entry.useOther) return (entry.other || '').trim();
  const selected = entry.selected || [];
  if (!selected.length) return '';
  return question.multiSelect ? selected.join(JOIN) : selected[0];
}

/**
 * The `answers` payload — only the questions that HAVE an answer.
 *
 * An unanswered question is omitted rather than sent blank: the backend names the
 * gaps back to the agent so it defaults exactly those and flags them `assumed`,
 * which is more useful than an empty string it has to interpret.
 */
export function answerPayload(questions, draft) {
  const out = [];
  for (const q of questions || []) {
    const value = answerFor(q, draft?.[q.questionId]);
    if (value) out.push({ questionId: q.questionId, value });
  }
  return out;
}

/** How many of the round's questions currently have an answer. */
export function answeredCount(questions, draft) {
  return answerPayload(questions, draft).length;
}

/**
 * Is the round complete? Used only to label Submit, never to DISABLE it.
 *
 * Partially answering is legitimate — the agent defaults the rest and says which —
 * so a Submit the user cannot press until every box is filled would turn an
 * optional round into a mandatory form.
 */
export function isComplete(questions, draft) {
  const total = (questions || []).length;
  return total > 0 && answeredCount(questions, draft) === total;
}
